import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CallService } from 'src/app/services/learner/call.service';
import { correctTranscript } from 'src/app/utils/transcript-corrector';
import { correctedTranscript } from 'src/app/utils/transcript-corrector';
import { AppConstants } from 'src/app/app-constants';
import { CallStoreService } from '../call-store/call-store.service';
import { ControlService } from '../control.service';
import { DeviceSettingsService } from '../device-settings.service';
import { CallValidationService } from '../call-validation/call-validation.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { SplitIoService } from 'src/app/services/external-tools/split-io.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AssemblyService {
  restartTranscriberOnProcess: boolean = false;
  callRestartTranscriber: boolean = false;
  isPauseOnProcess: boolean = false;
  isResumeOnProcess: boolean = false;
  isErrorOnAudioProcessor: boolean = false;
  retryGetTokenCount: number = 0;
  isStopAudioAPIOnProcess: boolean = false;

  mediaStream: MediaStream | null = null;
  audioContext: AudioContext | null = null;
  audioWorkletNode: AudioWorkletNode | null = null;
  mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  audioBufferQueue: Int16Array = new Int16Array(0);
  assemblyAIToken: string | null = null;
  websocket: WebSocket | null = null;

  isInternetConnected: boolean = true;
  partial: string = '';
  final: string = '';
  lastPartialUsed: string = '';
  shouldTranscriberRun: boolean = true;
  isStopRecordingInProgress: boolean = false;
  assemblyTurnSettingsEnabled: boolean = false;

  constructor(
    private callService: CallService,
    private callStore: CallStoreService,
    private controlService: ControlService,
    private deviceSettingsService: DeviceSettingsService,
    private callValidationService: CallValidationService,
    private logger: LoggerService,
    private splitIoService: SplitIoService
  ) {}

  async init(): Promise<void> {
    this.callStore.setIsAssemblyInitialized(true);
    // Reset any in-progress restart flag from a previous call that was abandoned
    // before its connectTranscriber onopen handler could clear it.
    this.restartTranscriberOnProcess = false;
    await this.stop();
    this.assemblyAIToken = await this.getToken();

    // Set call_uuid attribute for Split.io targeting
    const callUuid = this.callStore.getCallUuid();
    this.splitIoService.setAttributes({ call_uuid: callUuid });

    // Get assembly turn settings feature flag - when off, Assembly's formatted turn responses are ignored
    const treatment = await this.splitIoService.getTreatment(AppConstants.ASSEMBLY_TURN_SETTINGS);
    this.assemblyTurnSettingsEnabled = treatment === 'on';
    this.logger.log('[CALL] assembly - init', {
      assemblyTurnSettingsEnabled: this.assemblyTurnSettingsEnabled,
    });
  }

  async restartTranscriber(startRecording: boolean = true): Promise<void> {
    if (!this.callStore.getIsAssemblyInitialized()) {
      this.logger.log(
        '[CALL] assembly - init has not started yet, triggering.'
      );
      await this.init();
      await this.restartTranscriber();
      return;
    }
    console.log(
      `AssemblyAI ::: restartTranscriber - shouldTranscriberRun: ${this.shouldTranscriberRun}`
    );
    const isCallActive = this.callValidationService.isCallActive();

    this.logger.log('[CALL] assembly - reset', {
      restartTranscriberOnProcess: this.restartTranscriberOnProcess,
      isCallActive,
      hasToken: !!this.assemblyAIToken,
    });

    if (this.restartTranscriberOnProcess) {
      console.log(
        `AssemblyAI ::: Restart transcriber is blocked, restartTranscriberOnProcess: ${this.restartTranscriberOnProcess}`
      );
      return;
    }

    if (!isCallActive) {
      console.log(
        `AssemblyAI ::: Restart transcriber blocked — user not in active call.`
      );
      await this.stop();
      return;
    }
    console.log(`AssemblyAI ::: Restart transcriber`);
    this.callRestartTranscriber = false;
    this.restartTranscriberOnProcess = true;
    await this.stop();

    if (!this.assemblyAIToken) {
      this.assemblyAIToken = await this.getToken();
    }
    console.log(
      `AssemblyAI ::: Restart transcriber, startRecording: ${startRecording}`
    );
    await this.startTranscriber(startRecording);
  }

  async startTranscriber(startRecording: boolean = true): Promise<void> {
    this.isStopRecordingInProgress = false;
    this.logger.log('[CALL] assembly - start transcriber', { startRecording });

    // Validate AssemblyAI token exists
    if (!this.assemblyAIToken) {
      this.logger.error(
        '[CALL] assembly - AssemblyAI token is not available on startTranscriber()'
      );
      this.restartTranscriber(startRecording);
      return;
    }

    // Close existing websocket before creating a new one
    if (this.websocket) {
      const state = this.websocket.readyState;
      this.logger.log(
        '[CALL] assembly - Existing websocket found before startTranscriber',
        {
          readyState: state,
        }
      );
      // Close if websocket is connecting or open
      if (state <= 1) {
        this.websocket.close();
      }
      if (state === 2) {
        this.logger.log(
          '[CALL] assembly - websocket is closing, retrying startTranscriber in 50ms'
        );
        setTimeout(() => {
          this.startTranscriber(startRecording);
        }, 50);
        return;
      }
      this.websocket = null;
    }

    // Keyterms prompting is not supported with multilingual streaming.
    const keyterms = this.callStore.getCallWords() || [];
    const keytermsParam =
      keyterms.length > 0
        ? `&keyterms_prompt=${encodeURIComponent(JSON.stringify(keyterms))}`
        : '';

    // Determine speech model based on language
    // Reference: https://www.assemblyai.com/docs/universal-streaming/multilingual-transcription#supported-languages
    const language = this.callStore.getLanguage().toLowerCase();
    const speech_model =
      language === 'en' || language === 'english' || language === ''
        ? 'universal-streaming-english'
        : 'universal-streaming-multilingual';
    this.logger.log(
      `[CALL] assembly -  Using speech model: ${speech_model} for language: ${language}`
    );

    // Build WebSocket URL with parameters
    const endUtteranceSilenceThreshold = 600; // milliseconds to wait for silence before ending the phrase and adding punctuation
    const wsUrl =
      `${environment.assemblyAiStreamingUrl}?` +
      `sample_rate=16000&` +
      `min_end_of_turn_silence_when_confident=${endUtteranceSilenceThreshold}&` +
      `speech_model=${speech_model}&` +
      `format_turns=${this.assemblyTurnSettingsEnabled}&` +
      `token=${this.assemblyAIToken}${keytermsParam}`;

    // Create new WebSocket connection
    this.websocket = new WebSocket(wsUrl);

    // Setup WebSocket event handlers
    this.setupWebSocketHandlers(startRecording);
  }

  private setupWebSocketHandlers(startRecording: boolean): void {
    if (!this.websocket) {
      this.logger.log(
        '[CALL] assembly - setupWebSocketHandlers websocket empty'
      );
      return;
    }

    // Capture reference to detect stale handlers from old connections
    const ws = this.websocket;

    // Handle WebSocket connection opened
    this.websocket.onopen = async (event: Event) => {
      if (this.websocket !== ws) {
        this.logger.log('[CALL] assembly - ignoring stale onopen, new websocket already active');
        return;
      }
      this.logger.log('[CALL] assembly - open', {
        event,
        websocket: this.websocket,
      });

      // Start recording once connection is established
      if (startRecording && this.websocket) {
        this.connectTranscriber(startRecording);
      }
    };

    // Handle incoming messages
    this.websocket.onmessage = (event: MessageEvent) => {
      if (this.websocket !== ws) return;
      const turn = JSON.parse(event.data);
      console.log('AssemblyAI ::: Message turn', turn);

      if (this.isStopRecordingInProgress) return;
      const currentView = this.callStore.getCurrentView() || null;
      const isPracticeView = currentView == AppConstants.VIEW_PRACTICE;
      const isCallEnded = this.callStore.getDisplayPostCall();
      if (!isPracticeView || isCallEnded) {
        console.log(
          `AssemblyAI ::: Skipping transcript handling, currentView: ${currentView}, isCallEnded: ${isCallEnded}`
        );
        this.stopRecording();
        return;
      }

      // Prevent transcribing if the call is paused
      const isCallPaused = this.callStore.getIsPaused();
      if (isCallPaused) {
        console.log(`AssemblyAI ::: Call is paused, triggering pause...`);
        this.pause();
        return;
      }

      if (!turn.transcript) return;

      const { end_of_turn, transcript, turn_is_formatted } = turn;
      console.log(
        `AssemblyAI ::: (end_of_turn: ${end_of_turn}, turn_is_formatted: ${turn_is_formatted}): ${transcript}`,
      );
      this.handleTranscript(transcript, end_of_turn, turn_is_formatted);
    };

    // Handle WebSocket errors
    this.websocket.onerror = async (error: any) => {
      if (this.websocket !== ws) {
        this.logger.log('[CALL] assembly - ignoring stale onerror, new websocket already active');
        return;
      }
      const msg = error.message || '';
      this.logger.error('[CALL] assembly - error', error, {
        error: msg,
      });

      if (msg.includes('Session idle for too long')) {
        this.logger.error(
          '[CALL] assembly - Session idle for too long, restarting transcriber...'
        );
        this.callRestartTranscriber = true;
        await this.close();
        return;
      }

      if (
        msg.includes('Socket is not open for communication') &&
        this.restartTranscriberOnProcess
      ) {
        console.log(
          'AssemblyAI ::: Socket is not open for communication, waiting for restart...'
        );
        return;
      }

      // Validate if the error is due to not authorized
      const errMsg = (error.message || '').toLowerCase();
      if (errMsg.includes('unauthorized') || errMsg.includes('expired')) {
        this.restartTranscriberOnProcess = false;
        this.logger.error(
          '[CALL] assembly - Not authorized, restarting transcriber...',
          {
            errMsg,
          }
        );
        this.assemblyAIToken = '';
        this.restartTranscriber(false);
        return;
      }
      this.logger.error('[CALL] assembly - error', { error });

      // All recovery mechanisms failed or error is unhandled - show feedback to user
      // Only show modal if call is not ending or already ended
      if (
        this.controlService &&
        !this.callStore.getDisplayPostCall() &&
        !this.controlService.isEndCallTriggered
      ) {
        this.controlService.clickOpenNoResponse();
      }
    };

    // Handle WebSocket connection closed
    this.websocket.onclose = async (event: CloseEvent) => {
      // Skip if a newer connection already replaced this one
      const isStale = this.websocket !== null && this.websocket !== ws;
      if (isStale) {
        this.logger.log('[CALL] assembly - ignoring stale onclose, new websocket already active');
        return;
      }
      this.websocket = null;
      const reason = event.reason.toLowerCase();
      this.logger.log(
        `[CALL] assembly - Connection closed - Code: ${event.code} - Reason: ${reason}`,
        {
          event,
        }
      );
      // Handle - Unauthorized Connection: Signature has expired
      if (reason.includes('expired') || reason.includes('unauthorized')) {
        this.logger.log(`[CALL] assembly - handle token expired`);
        this.callRestartTranscriber = true;
        this.assemblyAIToken = '';
      }
      // Always clear the websocket reference when connection closes
      const shouldRestart = this.callRestartTranscriber;
      this.logger.log('[CALL] assembly - websocket set to null', {
        shouldRestart,
      });

      if (shouldRestart) {
        await this.stopAudioAPI();
        this.restartTranscriber();
      }
    };
  }

  async connectTranscriber(startRecording: boolean = true): Promise<void> {
    // Skip if restart is in progress and websocket not ready yet
    if (this.restartTranscriberOnProcess && !this.websocket) {
      this.logger.log('[CALL] assembly - connectTranscriber skipped, restart in progress and no websocket');
      return;
    }
    const readyState = this.websocket?.readyState || null;
    this.logger.log('[CALL] assembly - connect transcriber', {
      readyState,
      websocket: this.websocket,
      startRecording,
      restartTranscriberOnProcess: this.restartTranscriberOnProcess,
    });
    if (readyState !== 1) {
      // Connection failed - trigger modal
      this.logger.error('[CALL] assembly - connect failed, socket not open', {
        readyState,
      });
      if (
        this.controlService &&
        !this.callStore.getDisplayPostCall() &&
        !this.controlService.isEndCallTriggered
      ) {
        this.controlService.clickOpenNoResponse();
      }
      return;
    }
    if (startRecording) {
      try {
        // Check if audio context exists and is suspended (paused)
        const audioContextState = this.audioContext?.state || '';
        const isAudioContextSuspended = audioContextState === 'suspended';
        this.logger.log(
          `[CALL] assembly - connect transcriber - startRecording - audioContextState [${audioContextState}]`,
          {
            isAudioContextSuspended,
            audioContextState,
            audioContext: this.audioContext,
            audioWorkletNode: this.audioWorkletNode,
          }
        );
        const audio = this.audioContext && this.audioWorkletNode;
        if (audioContextState === 'running' && audio) {
          console.log('AssemblyAI ::: Audio context is running.');
          return;
        }
        if (isAudioContextSuspended && audio) {
          // Resume existing audio context instead of starting fresh
          console.log('AssemblyAI ::: Audio context is suspended, resuming...');
          await this.resume();
        } else {
          // Start fresh recording
          console.log(
            'AssemblyAI ::: Starting recording after connecting to websocket, then sending audio data.'
          );
          await this.startRecording((audioData: any) => {
            this.sendAudioData(audioData);
          });
        }
      } catch (error) {
        this.logger.error('[CALL] assembly - connect transcriber error', error);
      }
    }
    this.restartTranscriberOnProcess = false;
  }

  sendAudioData(audioData: any): void {
    if (this.callRestartTranscriber || this.restartTranscriberOnProcess) {
      this.logAudioBlocked(
        `AssemblyAI ::: sendAudioData is blocked, restarting transcriber, callRestartTranscriber: ${this.callRestartTranscriber}, restartTranscriberOnProcess: ${this.restartTranscriberOnProcess}`
      );
      return;
    }

    const isMicOff = !this.callStore.getIsMicOn();
    if (isMicOff) {
      this.logAudioBlocked(
        `AssemblyAI ::: Mic is off, not sending audio data to AssemblyAI`
      );
      return;
    }

    if (!this.websocket) {
      this.logAudioBlocked(
        `AssemblyAI ::: websocket is empty on sendAudioData(), retrying...`
      );
      this.restartTranscriber(false);
      return;
    }

    const state = this.websocket.readyState;
    if (state !== 1) {
      this.logAudioBlocked(
        `AssemblyAI ::: websocket is not open for communication, current state: ${state} on sendAudioData()`
      );
      // Only restart if fully closed, not if still connecting or closing
      if (state === 3) {
        this.restartTranscriber(false);
      }
      return;
    }
    try {
      this.websocket.send(audioData);
    } catch (error: any) {
      if (error.message.includes('Socket is not open for communication')) {
        this.logAudioBlocked(
          `AssemblyAI ::: websocket is not open for communication on sendAudioData(), retrying...`
        );
        this.restartTranscriber(false);
        return;
      }
      this.logger.error('[CALL] assembly - Error sending audio data:', error);
    }
  }

  logAudioBlocked(message: string) {
    console.log(message);
    this.logger.log('[CALL] assembly - send audio data blocked', {
      callRestartTranscriber: this.callRestartTranscriber,
      restartTranscriberOnProcess: this.restartTranscriberOnProcess,
      isMicOff: !this.callStore.getIsMicOn(),
      socketState: this.websocket?.readyState,
      message,
    });
  }

  handleTranscript(
    transcript: string,
    end_of_turn: boolean,
    turn_is_formatted: boolean
  ): void {
    let shouldTranscribeCall = this.shouldTranscribe();
    const state = this.audioContext?.state || '';
    const isMicOff = !this.callStore.getIsMicOn();

    if (!this.shouldTranscriberRun) {
      console.log(
        'AssemblyAI ::: shouldTranscriberRun is false, ignoring transcript.'
      );
      return;
    }
    if (state === 'suspended') {
      shouldTranscribeCall = false;
      console.log(`AssemblyAI ::: Audio context is suspended, skipping...`);
      return;
    }
    if (isMicOff) {
      console.log(
        `AssemblyAI ::: Mic is off, ignoring transcript: end_of_turn: ${end_of_turn} turn_is_formatted ${turn_is_formatted}:[${transcript}]`
      );
      return;
    }
    if (turn_is_formatted !== this.assemblyTurnSettingsEnabled) return;

    if (transcript && shouldTranscribeCall) {
      this.callStore.setLastMessageTimeStamp(new Date());
      this.callStore.setUserSpoke(Date.now());
      const isFinal = end_of_turn;
      if (isFinal) {
        console.log('Original Transcript: ' + transcript);
        const correctedTranscript: correctedTranscript = correctTranscript(
          transcript,
          this.callStore.getCallWords(),
          this.callStore.getReplaceWords()
        );
        this.logger.log('[CALL] assembly - transcript', {
          assemblyTurnSettingsEnabled: this.assemblyTurnSettingsEnabled,
          isFinal,
          end_of_turn,
          turn_is_formatted,
          transcript,
          state: this.websocket?.readyState || 'unknown',
          isMicOn: this.callStore.getIsMicOn(),
          originalText: transcript,
          correctedTranscript: correctedTranscript.transcript,
        });

        this.callStore.setRecognizedTranscripts([
          ...this.callStore.getRecognizedTranscripts(),
          correctedTranscript.transcript,
        ]);

        this.callStore.addTranscriptChangesLogs(correctedTranscript.changes);

        this.callStore.setSpeechTranscript(
          this.callStore.getRecognizedTranscripts().join(' ')
        );
        this.callStore.setPartialTranscriptExists(false);
        this.callStore.setSpeechTranscriptPartial('');
      } else {
        this.callStore.setPartialTranscriptExists(true);
        this.callStore.setSpeechTranscriptPartial(transcript);
        this.logger.log('[CALL] assembly - transcript partial', {
          assemblyTurnSettingsEnabled: this.assemblyTurnSettingsEnabled,
          isFinal,
          end_of_turn,
          turn_is_formatted,
          transcript,
          state: this.websocket?.readyState || 'unknown',
          isMicOn: this.callStore.getIsMicOn(),
          originalText: transcript,
        });
      }

      // required for manual - prevent unblocking during answer processing
      if (
        this.callStore.getIsContinueButtonBlocked() &&
        !this.callStore.getIsSendingAnswer() &&
        !this.callStore.getIsAvatarSpeaking() &&
        (!this.controlService?.isVisualAidCall ||
          !this.callStore.getIsProcessingAnswer())
      ) {
        console.log(
          `AssemblyAI ::: Unblocking continue button in required for manual check,
          isContinueButtonBlocked: ${this.callStore.getIsContinueButtonBlocked()},
          isSendingAnswer: ${this.callStore.getIsSendingAnswer()},
          isAvatarSpeaking: ${this.callStore.getIsAvatarSpeaking()},
          isProcessingAnswer: ${this.callStore.getIsProcessingAnswer()}`
        );
        this.callStore.setIsContinueButtonBlocked(false);
      }
    } else {
      console.log(
        `DO NOT SEND! (late response)\n end_of_turn: ${end_of_turn} turn_is_formatted ${turn_is_formatted}:[${transcript}]\n speechTranscript: [${this.callStore.getSpeechTranscript()}]\n speechTranscriptPartial: [${this.callStore.getSpeechTranscriptPartial()}]`
      );
    }
  }

  shouldTranscribe(): boolean {
    /* NOTE: keep repetitive checks for better understending */
    const isPaused = this.callStore.getIsPaused();
    const isMicOff = !this.callStore.getIsMicOn();
    if (isPaused || isMicOff) {
      return false;
    }

    const manual = AppConstants.ADVANCE_TYPE_MANUAL;
    const isManual = this.callStore.getAdvanceType() == manual;
    const isSendingAnswer = this.callStore.getIsSendingAnswer();
    const isWaitingTimeout = this.callStore.getIsWaitingTimeout();
    const isAvatarSpeaking = this.callStore.getIsAvatarSpeaking();

    // MANUAL CALLS
    if (isManual) {
      if (isSendingAnswer) {
        const isConversational = this.controlService.isConversational();
        if (isConversational) {
          return false;
        }
      }
      if (isWaitingTimeout) {
        return true;
      }
      if (isAvatarSpeaking) {
        return false;
      }
    }

    // AUTOMATIC CALLS
    if (!isManual) {
      if (isSendingAnswer) {
        return false;
      }
    }
    return true;
  }

  async stop(): Promise<void> {
    this.logger.log('[CALL] assembly - stop');
    await this.stopRecording();
    if (this.controlService?.isVisualAidCall) {
      this.callStore.setIsProcessingAnswer(false);
    }
    if (this.websocket) {
      await this.close();
    }
  }

  async close(): Promise<void> {
    const state = this.websocket?.readyState || 9; // 9 to not affect next if condition
    this.logger.log('[CALL] assembly - close', {
      state,
    });
    if (this.websocket && state <= 1) {
      this.websocket.close();
    }
  }

  async requestMicrophoneStream(): Promise<MediaStream> {
    const constraints = this.getAudioConstraints();
    this.logger.log('[CALL] assembly - request microphone stream', {
      constraints,
    });
    return await navigator.mediaDevices.getUserMedia(constraints);
  }

  getAudioConstraints(): MediaStreamConstraints {
    const device = this.deviceSettingsService?.getAudioDeviceLocalStorage();
    const audioDeviceSaved = device || '';
    return audioDeviceSaved
      ? {
          audio: {
            deviceId: { exact: audioDeviceSaved },
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: true },
          },
          video: false,
        }
      : {
          audio: {
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: true },
          },
          video: false,
        };
  }

  async startRecording(callback: any): Promise<void> {
    const isCallActive = this.callValidationService.isCallActive();
    this.logger.log('[CALL] assembly - start recording', {
      isCallActive,
      shouldTranscriberRun: this.shouldTranscriberRun,
      isMediaStreamAvailable: !!this.mediaStream,
    });
    if (!isCallActive) {
      console.log(
        `AssemblyAI ::: User is not in an active call — startRecording canceled.`
      );
      return;
    }
    console.log('start recording: ', this.shouldTranscriberRun);
    if (!this.shouldTranscriberRun) {
      console.log(
        'AssemblyAI ::: Skipping restartTranscriber, call already ended.'
      );
      return;
    }
    console.log(
      `AssemblyAI ::: Starting recording mediaStream [${!!this.mediaStream}]`
    );
    // validate if the stream is already active
    if (!this.mediaStream) {
      this.mediaStream = await this.requestMicrophoneStream();
      const audioTracks = this.mediaStream.getAudioTracks();
      this.logger.log(
        `[CALL] assembly - Requesting microphone stream on startRecording`,
        { audioTracks }
      );
      if (audioTracks.length > 0) {
        audioTracks[0].addEventListener('ended', () => {
          this.logger.error(`[CALL] assembly -  Audio track disconnected`);
          this.callStore.setIsAudioDeviceSelected(false);
          this.deviceSettingsService.removeAudioDeviceLocalStorage();
          this.controlService.clickOpenNoResponse();
        });
      }
    }
    console.log(`AssemblyAI ::: Creating audio context`);

    // init audio context
    this.audioContext = new AudioContext({
      latencyHint: 'balanced',
      sampleRate: 48000,
    });

    // create a media stream source
    this.mediaStreamSource = this.audioContext.createMediaStreamSource(
      this.mediaStream
    );

    // add the audio processor to the audio context
    await this.audioContext.audioWorklet.addModule(
      'assets/js/audio-processor.js'
    );

    // create the audio worklet node
    console.log(`AssemblyAI ::: Creating audio worklet node`);
    this.audioWorkletNode = new AudioWorkletNode(
      this.audioContext,
      'audio-processor'
    );

    // connect the media stream source to the audio worklet node
    console.log(`AssemblyAI ::: Connecting audio source to audio worklet node`);
    this.mediaStreamSource.connect(this.audioWorkletNode);
    this.audioWorkletNode.connect(this.audioContext.destination);

    // listen for audio data from the audio worklet node
    console.log(`AssemblyAI ::: Listening for audio data`);
    this.audioWorkletNode.port.onmessage = async (event) => {
      if (event.data.error) {
        this.logger.error(
          '[CALL] assembly - workletnode error received from audio processor',
          { error: event.data.error }
        );
        this.isErrorOnAudioProcessor = true;
        this.validateWebAudioStatus();
        return;
      }
      if (this.restartTranscriberOnProcess) {
        return;
      }
      this.isErrorOnAudioProcessor = false;
      this.processAudioData(event, callback);
    };

    this.audioContext.addEventListener('statechange', () => {
      console.log(
        `AssemblyAI ::: Audio context state changed to ${this.audioContext?.state}`
      );
    });
  }

  processAudioData(event: any, callback: any): void {
    // Check if mic is off - don't process audio data if mic is off
    const isMicOff = !this.callStore.getIsMicOn();
    if (isMicOff) {
      // console.log(`AssemblyAI ::: Mic is off, not processing audio data`);
      return;
    }

    const currentBuffer = new Int16Array(event.data.audio_data);
    // merge the audio buffers together to create a single buffer of audio data
    this.audioBufferQueue = this.mergeBuffers(
      this.audioBufferQueue,
      currentBuffer
    );

    if (this.audioContext) {
      const sampleRate = this.audioContext.sampleRate;
      const abQueueLength = this.audioBufferQueue.length;
      const bufferDuration = (abQueueLength / sampleRate) * 1000; // in ms

      // wait until we have 100ms of audio data
      if (bufferDuration >= 100) {
        const totalSamples = Math.floor(this.audioContext.sampleRate * 0.1);

        // get the first 100ms of audio data
        const finalBuffer = new Uint8Array(
          this.audioBufferQueue.subarray(0, totalSamples).buffer
        );

        // remove the first 100ms of audio data
        this.audioBufferQueue = this.audioBufferQueue.subarray(totalSamples);

        // send the audio data to the callback
        if (callback) callback(finalBuffer);
      }
    }
  }

  mergeBuffers(left: Int16Array, right: Int16Array): Int16Array {
    const result = new Int16Array(left.length + right.length);
    result.set(left, 0);
    result.set(right, left.length);
    return result;
  }

  async stopRecording(): Promise<void> {
    this.logger.log('[CALL] assembly - stop recording');

    this.isStopRecordingInProgress = true;
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = null;
    }
    await this.stopAudioAPI();
  }

  async stopAudioAPI(): Promise<void> {
    this.logger.log('[CALL] assembly - stop audio API');
    this.audioBufferQueue = new Int16Array(0);
    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }
    if (this.audioWorkletNode) {
      this.audioWorkletNode.disconnect();
      this.audioWorkletNode = null;
    }
    if (this.audioContext) {
      const state = this.audioContext?.state || '';
      if (state === 'closed') {
        this.logger.log(`[CALL] assembly - Audio context closed already`);
        return;
      }
      if (this.isStopAudioAPIOnProcess) {
        return;
      }
      this.isStopAudioAPIOnProcess = true;
      await this.audioContext.close();
      this.logger.log('[CALL] assembly - audioContext closed');
      this.isStopAudioAPIOnProcess = false;
      this.audioContext = null;
    }
    this.audioBufferQueue = new Int16Array(0);
    this.isResumeOnProcess = false;
  }

  async getToken(): Promise<string> {
    const retryLimit = 5;
    try {
      // Convert the observable to a promise and await the result
      const response = await firstValueFrom(
        this.callService.getAssemblyAiToken()
      );

      this.logger.log('[CALL] assembly - get token', {
        response,
      });

      this.retryGetTokenCount = 0;
      return response.token;
    } catch (error: Error | any) {
      this.logger.error('[CALL] assembly - get token - error', error, {
        error: error.message,
      });
      if (this.retryGetTokenCount < retryLimit) {
        console.warn(`Error fetching Assembly AI token, retrying...`);
        this.retryGetTokenCount++;
        setTimeout(() => {
          this.getToken();
        }, 1000);
      } else {
        this.logger.error(
          `[CALL] assembly - Error fetching Assembly AI token, we reach our retry limit of ${retryLimit}`
        );
      }
      throw error;
    }
  }

  async pause(): Promise<boolean> {
    this.logger.log('[CALL] assembly - audio API pause');
    if (this.audioContext && !this.isPauseOnProcess) {
      const state = this.audioContext?.state || '';
      if (state === 'suspended' || state === 'closed') {
        return false;
      }
      this.audioBufferQueue = new Int16Array(0);
      this.isPauseOnProcess = true;
      await this.audioContext.suspend();
      this.audioBufferQueue = new Int16Array(0);
      console.log(`AssemblyAI ::: Audio context suspended`);
      this.isPauseOnProcess = false;
      return true;
    }
    return false;
  }

  async resume(): Promise<boolean> {
    this.logger.log('[CALL] assembly - audio API resume', {
      audioContext: this.audioContext,
      audioWorkletNode: this.audioWorkletNode,
      isResumeOnProcess: !this.isResumeOnProcess,
      state: this.audioContext?.state || '',
    });
    if (this.audioContext && this.audioWorkletNode && !this.isResumeOnProcess) {
      const state = this.audioContext?.state || '';
      if (state === 'running') {
        return false;
      }
      this.isResumeOnProcess = true;
      this.audioBufferQueue = new Int16Array(0);
      try {
        await this.audioContext.resume();
        console.log(`AssemblyAI ::: Audio context resumed`);
      } catch (error) {
        console.warn(`AssemblyAI ::: Error resuming audio context: ${error}`);
      }
      if (!this.websocket) {
        await this.startTranscriber(false);
      }
      this.isResumeOnProcess = false;
      return true;
    }
    return false;
  }

  getAudioContextState(): string {
    return this.audioContext?.state || '';
  }

  forceEndUtterance(): void {
    this.logger.log('[CALL] assembly - force end statement');
    if (this.websocket && this.websocket.readyState === 1) {
      this.websocket.send(JSON.stringify({ type: 'ForceEndpoint' }));
      this.audioBufferQueue = new Int16Array(0);
      console.log(`AssemblyAI ::: Force end utterance`);
    }
  }

  async validateWebAudioStatus(isRestart: boolean = false): Promise<void> {
    if (!isRestart) {
      this.callStore.setIsErrorOnAudioValidation(false);
    }

    // validate if internet is connected
    console.log(
      `Validate AssemblyAI ::: Internet connected - is on line? ${navigator.onLine}`
    );
    this.isInternetConnected = navigator.onLine;
    if (!navigator.onLine) {
      this.callStore.setIsLoadingNoResponse(true);
      // show loading spinner in modal to let user know we did check the internet connection
      setTimeout(() => {
        this.callStore.setIsLoadingNoResponse(false);
      }, 350);
      this.logger.log('[CALL] assembly - validate status', {
        statusMessage: `Internet is not connected`,
        functionTriggered: 'show modal setIsLoadingNoResponse',
      });
      return;
    }

    this.checkIfSendAnswerAgain();

    // validate if audio device is set
    const audioDeviceSelected =
      this.deviceSettingsService.getAudioDeviceLocalStorage();
    const isAudioDeviceSelected = audioDeviceSelected ? true : false;
    this.callStore.setIsAudioDeviceSelected(isAudioDeviceSelected);
    console.log(
      `Validate AssemblyAI ::: Audio device is selected - ${audioDeviceSelected}`
    );
    if (!audioDeviceSelected) {
      this.callStore.setIsErrorOnAudioValidation(true);
      this.logger.warn('[CALL] assembly - validate status', {
        statusMessage: 'No audio device is selected',
        functionTriggered: '',
      });
      return;
    }

    // show loading spinner in modal
    this.callStore.setIsLoadingNoResponse(true);

    // validate if assembly ai token is available
    if (!this.assemblyAIToken || this.assemblyAIToken.length === 0) {
      this.logger.warn(
        '[CALL] assembly - validate status - token is not available',
        {
          statusMessage: 'Token is not available',
          functionTriggered: 'getToken',
        }
      );
      this.assemblyAIToken = await this.getToken();
      this.callStore.setIsErrorOnAudioValidation(true);
      this.validateWebAudioStatus(true);
      return;
    }

    // validate if is transcriber available
    if (!this.websocket) {
      this.logger.warn(
        '[CALL] assembly - validate status - websocket is not available',
        {
          statusMessage: 'websocket is not available',
          functionTriggered: 'startTranscriber',
        }
      );
      await this.startTranscriber(false);
      this.callStore.setIsErrorOnAudioValidation(true);
      this.validateWebAudioStatus(true);
      return;
    }

    // Validate if assembly socket connection is open
    const readyState = this.websocket.readyState;
    this.logger.warn(
      `[CALL] assembly - validate status - websocket ready state: ${readyState}`,
      {
        statusMessage: `websocket ready state: ${readyState}`,
        functionTriggered: '',
      }
    );
    if (readyState != 1) {
      const isSocketConnecting = readyState === 0;
      if (isSocketConnecting) {
        this.logger.warn(
          '[CALL] assembly - validate status - websocket is connecting, please wait...',
          {
            statusMessage: 'Socket is connecting, please wait...',
            functionTriggered: '',
          }
        );
        this.callStore.setIsErrorOnAudioValidation(true);
        // check again after 1 second to see if connection now is established
        setTimeout(() => {
          this.validateWebAudioStatus(true);
        }, 1000);
        return;
      }

      const isSocketClosing = readyState === 2;
      const isSocketClosed = readyState === 3;
      if (isSocketClosing || isSocketClosed) {
        const msg = `websocket is ${isSocketClosed ? 'closed' : 'closing'}`;
        this.logger.warn('[CALL] assembly - validate status - ' + msg, {
          statusMessage: msg,
          functionTriggered: 'startTranscriber',
        });
        await this.startTranscriber(false);
        this.callStore.setIsErrorOnAudioValidation(true);
        this.validateWebAudioStatus(true);
        return;
      }
    }

    // is media stream source available
    if (!this.mediaStreamSource) {
      this.logger.warn(
        '[CALL] assembly - validate status - Media stream source is not available',
        {
          statusMessage: 'Media stream source is not available',
          functionTriggered: 'stopAudioAPI, connectTranscriber',
        }
      );
      await this.stopAudioAPI();
      await this.connectTranscriber();
      this.updateValidationState();
      return;
    }

    // is media stream source state suspended or closed
    const mssState = this.mediaStreamSource.context?.state || '';
    if (mssState === 'suspended' || mssState === 'closed') {
      const msg = `Media stream source state is ${mssState}`;
      this.logger.warn('[CALL] assembly - validate status - ' + msg, {
        statusMessage: msg,
        functionTriggered: 'stopAudioAPI, connectTranscriber',
      });
      await this.stopAudioAPI();
      await this.connectTranscriber();
      this.updateValidationState();
      return;
    }

    // is audio recording
    if (!this.audioContext) {
      const msg = 'Audio context is not available';
      this.logger.warn('[CALL] assembly - validate status - ' + msg, {
        statusMessage: msg,
        functionTriggered: 'stopAudioAPI, connectTranscriber',
      });
      await this.stopAudioAPI();
      await this.connectTranscriber();
      this.updateValidationState();
      return;
    }

    // is audio context state suspended or closed
    const audioContextState = this.audioContext.state || '';
    console.log(
      `Validate AssemblyAI ::: Audio context state: ${audioContextState}`
    );
    if (audioContextState === 'suspended' || audioContextState === 'closed') {
      const msg = `Audio context is ${audioContextState}`;
      this.logger.warn('[CALL] assembly - validate status - ' + msg, {
        statusMessage: msg,
        functionTriggered: 'stopAudioAPI, connectTranscriber',
      });
      await this.stopAudioAPI();
      await this.connectTranscriber();
      this.updateValidationState();
      return;
    }

    // is audio worklet node available
    if (!this.audioWorkletNode) {
      const msg = 'Audio worklet node is not available';
      this.logger.warn('[CALL] assembly - validate status - ' + msg, {
        statusMessage: msg,
        functionTriggered: 'stopAudioAPI, connectTranscriber',
      });
      await this.stopAudioAPI();
      await this.connectTranscriber();
      this.updateValidationState();
      return;
    }

    // is audio worklet node state suspended or closed
    const awnState = this.audioWorkletNode.context?.state || '';
    console.log(
      `Validate AssemblyAI ::: Audio worklet node state: ${awnState}`
    );
    if (awnState === 'suspended' || awnState === 'closed') {
      const msg = `Validate AssemblyAI ::: Audio worklet node is ${awnState}`;
      this.logger.warn('[CALL] assembly - validate status - ' + msg, {
        statusMessage: msg,
        functionTriggered: 'stopAudioAPI, connectTranscriber',
      });
      await this.stopAudioAPI();
      await this.connectTranscriber();
      this.updateValidationState();
      return;
    }

    // is audio processor working
    if (this.isErrorOnAudioProcessor) {
      const msg = 'Error on audio processor';
      this.logger.warn('[CALL] assembly - validate status', {
        statusMessage: msg,
        functionTriggered: 'stopAudioAPI, connectTranscriber',
      });
      await this.stopAudioAPI();
      await this.connectTranscriber();
      this.updateValidationState();
      return;
    }

    this.logger.log('[CALL] assembly - validate status - WebAudio is ready!', {
      statusMessage: 'WebAudio is ready!',
      functionTriggered: '',
    });

    this.callStore.setIsLoadingNoResponse(false);
    this.updateValidationState();
  }

  updateValidationState(): void {
    this.callStore.setIsErrorOnAudioValidation(false);
    this.callStore.setIsLoadingNoResponse(false);
    this.callStore.setOpenNoResponseModal(false);
  }

  checkIfSendAnswerAgain(): void {
    if (this.callStore.getIsSendingAnswer()) {
      this.callStore.setIsAvatarThinking(false);
      console.log('AssemblyAI: Setting isSendingAnswer to false');
      this.callStore.setIsSendingAnswer(false);
      this.callStore.setWasSendingAnswer(false);
      const isConversational = this.controlService.isConversational();
      const answerData = this.callStore.getAnswerData();
      const practiceCallUuid = answerData?.practiceCallUuid;
      const questionUuid = answerData?.questionUuid;
      const answer = answerData?.answer;
      // send answer again if guided call
      if (!isConversational && practiceCallUuid && questionUuid && answer) {
        this.logger.log('[CALL] assembly - retry send answer', {
          questionUuid,
          answer,
        });
        this.callService
          .sendUserAnswer(practiceCallUuid, questionUuid, answer)
          .subscribe({
            next: () => {
              console.log(
                `AssemblyAI ::: Sending answer again after internet recover, answer: [${answer}] questionUuid: [${questionUuid}] practiceCallUuid: [${practiceCallUuid}]`
              );
            },
            error: (err) => {
              this.logger.error(`[CALL] assembly - sendUserAnswer`, err, {
                error: err,
              });
            },
          });
      }
      this.callStore.setAnswerData(null);
    }
  }

  getCurrentTranscription(): string {
    const final = this.final;
    const partial = this.partial;
    if (partial.length > 0) this.lastPartialUsed = partial;
    this.final = '';
    this.partial = '';
    console.log(final + ':::::::::::' + partial);

    return final + ' ' + partial;
  }

  removeSubstring(source: string, target: string): string {
    // Normalize by converting to lowercase and removing punctuation
    const normalizedSource = source.toLowerCase().replace(/[^\w\s]/g, '');
    const normalizedTarget = target.toLowerCase().replace(/[^\w\s]/g, '');

    // Remove the normalized target from the normalized source
    const result = normalizedSource.replace(
      new RegExp(normalizedTarget, 'gi'),
      ''
    ); // 'gi' for global and case-insensitive
    return result;
  }

  clearCurrentTranscriptionProperties() {
    this.final = '';
    this.partial = '';
  }
}
